import { ChangeEvent, Dispatch, useCallback, useEffect, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useField } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { format, isAfter, isBefore, parse } from 'date-fns';

import { ProfKindCodesEnum } from 'APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import {
  catalogOrdersRelationDictionariesGet,
  catalogPmConditionsGet,
} from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { IKnmReasonTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonTypeSelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { PM_REASONS } from 'CorePackage/MainModule/routes/routes';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { isAssignmentRequiredByType } from 'KnmPackage/MainModule/common/isAssignmentRequiredByType';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getPmReasonTypesSelectOptions,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsNew,
  getKnmOrdersSelectOptions,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsErknmStatusCommentsExist,
  getKnmWatchOnlyByTestUser,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { isPlanKnm } from 'KnmPmPackage/MainModule/common/isPlanKnm';
import {
  IS_UNABLE_TO_REFUSE_BLOCK_ID,
  REASONS_BLOCK_ID,
  getReasonBlockId,
} from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { IKnmPmFormReason } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormReason';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IDictionaryVersion } from 'APIPackage/DictionariesModule/interfaces/IDictionaryVersion';
import {
  fetchDictionaryValue,
  fetchIsUnableToRefuseFlagPm,
} from 'APIPackage/DictionariesModule/services/dictionariesApi';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { CHECKING_IT_ACCREDITATION } from 'CorePackage/MainModule/constants/notices';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { ErknmReasonProgramDocument } from 'KnmErknmPackage/ErknmReasonsModule/components/ErknmReasonProgramDocument';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';

import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import { getFullNameGuarantor } from '../common/getFullNameGuarantor';
import { IKnmOrderSelectOption } from '../../../KnmPackage/MainModule/interfaces/IKnmOrderSelectOption';
import styles from './PmReasons.module.scss';
import { PmReasonsLink } from './PmReasonsLink';
import { PmReasonsPlanKnmMessageModal } from './PmReasonsPlanKnmMessageModal';
import { ErknmLinks } from '../../../KnmErknmPackage/ErknmEventSubjectModule/components/Links/ErknmLinks';
import { knmGetOrders, knmSetPmIsUnableToRefuseFormValue } from '../../../KnmPackage/MainModule/actions/knmActions';
import { KnmReasonRiskIndicators } from '../../../KnmPackage/KnmReasonRiskIndicatorModule/components/KnmReasonRiskIndicators';
import { fetchWorkingDaysCount } from '../../../APIPackage/KnmModule/services/knmApi';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';

interface IPmReasonProps {
  className?: string;
  watchOnly: boolean;
  pmTypeCode: ProfKindCodesEnum | undefined;
  reasonsPm: IKnmPmFormReason[] | undefined;
  autoAddReason?: boolean;
  disabled?: boolean;
  initialReasonsPm?: IKnmPmFormReason[] | undefined;
}

interface IPmReasonsStoreProps {
  formRef: IKnmFormRef | undefined;
  knmReasonTypesSelectOptions: IKnmReasonTypeSelectOption[];
  knmOrders: IKnmOrderSelectOption[] | undefined;
  isSupervisor: boolean;
  settings: IAdminSetting[] | undefined;
  isNewKnm: boolean;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isKnmStatusCommentsExist: boolean;
  isWatchOnlyByTestUser: boolean;
}

const emptyAddingReason: IKnmPmFormReason = {
  reason: undefined,
  type: undefined,
  text: undefined,
  date: undefined,
  riskIndicators: undefined,
  requirementDetails: undefined,
  dateRequirement: undefined,
  assignmentNumber: undefined,
  assignmentDate: undefined,
  numGuid: undefined,
  dateCreate: undefined,
  dateBegin: undefined,
  dateEnd: undefined,
  order: undefined,
  knmIdLink: undefined,
  ervkUuid: undefined,
  links: undefined,
  listId: uuidv4(),
  planKnm: undefined,
  fullName: undefined,
};

const PmReasonsWithoutStore = ({
  className = '',
  watchOnly,
  formRef,
  knmReasonTypesSelectOptions,
  knmOrders,
  isSupervisor,
  pmTypeCode,
  settings,
  reasonsPm,
  isNewKnm,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isKnmStatusCommentsExist,
  isWatchOnlyByTestUser,
  autoAddReason = false,
  disabled = false,
  initialReasonsPm,
}: IPmReasonProps & IPmReasonsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const startDate = useField<string | undefined>('startDate').input.value;
  const stopDate = useField<string | undefined>('stopDate').input.value;
  const { knoOrganizationPm, supervisionTypePm } = formRef?.current.getState().values ?? {};

  const isDisabledReasonPlanKnm = watchOnly || !isSupervisor || !isKnmStatusFillInProcess;

  const values = formRef?.current.getState().values;
  const created = values?.created;

  const isLaterThanOrderInProd = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.VALIDATION_4_1_39_STARTS_FROM,
  );
  const isLaterThanReasonFieldsAvailabilitySetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.REASON_FIELDS_AVAILABILITY_4_1_54_STARTS_FROM,
  );
  const isShowCheckingProgram = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_41_STARTS_FROM,
  );

  const isReasonFieldsDisabled =
    (!isLaterThanReasonFieldsAvailabilitySetting
      ? isDisabledReasonPlanKnm
      : !(isSupervisor && (isNewKnm || isKnmStatusFillInProcess))) || isWatchOnlyByTestUser;

  const requiredDatesFilled =
    (startDate && pmTypeCode === ProfKindCodesEnum.OP) ||
    (startDate && stopDate && (pmTypeCode === ProfKindCodesEnum.PP || pmTypeCode === undefined));

  const getReasonsPlaceholder = useCallback(() => {
    if (pmTypeCode === ProfKindCodesEnum.OP || pmTypeCode === ProfKindCodesEnum.PP) {
      return 'Выберите основание проведения мероприятия';
    }

    return 'Выберите основание проведения профилактических мероприятий';
  }, [pmTypeCode]);

  useEffect(() => {
    async function checkWorkingDaysCount() {
      if (
        values?.startDate &&
        values?.stopDate &&
        values?.isUnableToRefuse &&
        (!values?.suspensionEvents ||
          values?.suspensionEvents.length === 0 ||
          !values?.eventsPm?.some(event => ['Экспертиза', 'Испытание'].includes(event.type?.title || '')))
      ) {
        try {
          const formatStartDate = parse(values?.startDate, 'dd.MM.yyyy', new Date());
          const formatStopDate = parse(values?.stopDate, 'dd.MM.yyyy', new Date());
          let workingDaysCountResponse: { count: number } = { count: 0 };

          if (isBefore(formatStartDate, formatStopDate)) {
            workingDaysCountResponse = await fetchWorkingDaysCount(
              format(parse(values?.startDate, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd'),
              format(parse(values?.stopDate, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd'),
            );
          }

          const workingDaysCount = (workingDaysCountResponse?.count as number) ?? 0;

          formRef?.current.change(`startAndStopDateCountError` as keyof IKnmPmForm, workingDaysCount > 10);
        } catch (e) {
          formRef?.current.change(`startAndStopDateCountError` as keyof IKnmPmForm, false);
        }
      } else {
        formRef?.current.change(`startAndStopDateCountError` as keyof IKnmPmForm, false);
      }
    }

    checkWorkingDaysCount();
  }, [values?.startDate, values?.stopDate, values?.isUnableToRefuse, values?.eventsPm, values?.suspensionEvents]);

  const appliedInitialReasonsRef = useRef(false);
  const ensuredTypesRef = useRef(false);

  useEffect(() => {
    if (!autoAddReason || watchOnly || !push) {
      return;
    }

    const currentFormReasons = formRef?.current.getState().values?.reasonsPm;
    const hasReasons =
      (Array.isArray(currentFormReasons) && currentFormReasons.length > 0) ||
      (Array.isArray(initialReasonsPm) && initialReasonsPm.length > 0) ||
      (Array.isArray(reasonsPm) && reasonsPm.length > 0);

    if (!hasReasons) {
      push('reasonsPm', emptyAddingReason);
    }
  }, [autoAddReason, push, watchOnly, formRef, initialReasonsPm, reasonsPm]);

  useEffect(() => {
    if (appliedInitialReasonsRef.current || !initialReasonsPm?.length || !formRef?.current) {
      return;
    }

    appliedInitialReasonsRef.current = true;
    formRef.current.change('reasonsPm', initialReasonsPm);
  }, [formRef, initialReasonsPm]);

  useEffect(() => {
    if (!formRef?.current || ensuredTypesRef.current) {
      return;
    }

    const formReasons: IKnmPmFormReason[] | undefined = formRef.current.getState().values?.reasonsPm;

    if (!Array.isArray(formReasons) || formReasons.length < 1) {
      return;
    }

    formReasons.forEach((reason, index) => {
      if (!reason) return;

      if (!reason.listId) {
        const initialReason = initialReasonsPm?.[index];

        if (initialReason?.listId) {
          formRef.current?.change(`reasonsPm[${index}].listId` as keyof IKnmPmForm, initialReason.listId);
        }
      }

      const resolvedType =
        reason.type || initialReasonsPm?.find(item => item.listId === reason.listId)?.type || reason.reason?.type;

      if (!reason.type && resolvedType) {
        formRef.current?.change(
          `reasonsPm[${index}].type` as keyof IKnmPmForm,
          (resolvedType as unknown) as IKnmPmForm[keyof IKnmPmForm],
        );
      }
    });

    ensuredTypesRef.current = true;
  }, [formRef, initialReasonsPm, values?.reasonsPm]);

  const getIsUnableToRefuse = async (reasonErvkUuid: string, orderId?: string, orderVersionId?: string) => {
    const isUnableToRefuse = await fetchIsUnableToRefuseFlagPm(reasonErvkUuid, orderId, orderVersionId);

    dispatch(knmSetPmIsUnableToRefuseFormValue(isUnableToRefuse));
    formRef?.current.change('isUnableToRefuse', isUnableToRefuse);
  };

  const handleReasonTypeChange = (index: number) => async (value: IKnmReasonTypeSelectOption) => {
    formRef?.current.change(`reasonsPm[${index}].reason` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].ervkUuid` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].shortTitle` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].title` as keyof IKnmPmForm);

    formRef?.current.change(`reasonsPm[${index}].requirementDetails` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].dateRequirement` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].assignmentNumber` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].assignmentDate` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].requirementDetails` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].riskIndicators` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].links` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].knmIdLink` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].planKnm` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].order` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].assignmentNumber` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].assignmentDate` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].programAttachment` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].programNumber` as keyof IKnmPmForm);

    if (value?.ervkUuid) {
      const dictionaryValue: IDictionaryVersion = await fetchDictionaryValue(DictionaryUUIDsEnum.ERVK, value.ervkUuid);

      const reasonRequirementsValue = JSON.parse(dictionaryValue.value);

      formRef?.current.change(`reasonRequirements` as keyof IKnmPmForm, reasonRequirementsValue);
      formRef?.current.change(`reasonsPm[${index}].ervkUuid` as keyof IKnmPmForm, value.ervkUuid);

      const isPresentInItRegistry = formRef?.current.getFieldState('isPresentInItRegistry')?.value;

      if (isPresentInItRegistry !== undefined && reasonRequirementsValue?.it !== null) {
        const hasItOrganization = isPresentInItRegistry;
        const hasNotItOrganization = !isPresentInItRegistry;

        if (
          (reasonRequirementsValue?.it && hasNotItOrganization) ||
          (!reasonRequirementsValue?.it && hasItOrganization)
        ) {
          dispatch(
            addNoticeAction({
              renderContent: CHECKING_IT_ACCREDITATION,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.CHECKING_IT_ACCREDITATION,
            }),
          );
        }
      }

      if (pmTypeCode === ProfKindCodesEnum.PP) {
        getIsUnableToRefuse(value?.ervkUuid);
      }
    }
    formRef?.current.change(`reasonsPm[${index}].fullName` as keyof IKnmPmForm);
    formRef?.current.change(`reasonsPm[${index}].text` as keyof IKnmPmForm);

    formRef?.current.change('isOrganizationNew');
    formRef?.current.change('ervkId');

    if (autoAddReason) {
      formRef?.current.change('pmType');
    }

    // @ts-ignore
    const objects: IKnmErknmFormObject[] | undefined = formRef?.current.getFieldState('objectsPm' as keyof IKnmPmForm)
      ?.value;

    formRef?.current.change('ervkId');
    if (!isNewKnm) {
      formRef?.current.change('subjectTypePm');
      formRef?.current.change('eventsPm', []);

      objects?.forEach((item, i) => {
        if (item.ervkId) {
          !watchOnly && remove && remove('objectsPm', i);
        } else {
          formRef?.current.change(`objectsPm[${i}].objectType` as keyof IKnmPmForm);
          formRef?.current.change(`objectsPm[${i}].objectKind` as keyof IKnmPmForm);
          formRef?.current.change(`objectsPm[${i}].objectSubKind` as keyof IKnmPmForm);
          formRef?.current.change(`objectsPm[${i}].riskCategory` as keyof IKnmPmForm);
          formRef?.current.change(`objectsPm[${i}].dangerClass` as keyof IKnmPmForm);
        }
      });
    }
  };

  const handleOrderChange = (index: number) => (value?: IKnmOrderSelectOption) => {
    const { number, date, guarantors, recordId, recordVersionId } = value || {};
    const objectsPm = formRef?.current.getFieldState('objectsPm')?.value;

    const reasonPm = formRef?.current.getFieldState(`reasonsPm`)?.value?.[index];

    dispatch(catalogOrdersRelationDictionariesGet(recordId, recordVersionId));

    const newObjectsPm = objectsPm
      ?.filter(objectPm => !objectPm.ervkId)
      .map(objectPm => ({
        ...objectPm,
        objectType: undefined,
        objectKind: undefined,
        objectSubKind: undefined,
        riskCategory: undefined,
        dangerClass: undefined,
      }));

    formRef?.current.change('objectsPm', newObjectsPm);
    formRef?.current.change('subjectTypePm');
    formRef?.current.change('inn');
    formRef?.current.change('snils');
    formRef?.current.change('organizationName');
    formRef?.current.change('ogrn');
    formRef?.current.change('isUnableToRefuse');
    formRef?.current.change('isOrganizationNew');
    formRef?.current.change('ervkId');

    formRef?.current.change(`reasonsPm[${index}].assignmentNumber` as keyof IKnmPmForm, number);
    formRef?.current.change(`reasonsPm[${index}].assignmentDate` as keyof IKnmPmForm, date);
    formRef?.current.change(`reasonsPm[${index}].fullName` as keyof IKnmPmForm, getFullNameGuarantor(guarantors?.[0]));

    if (reasonPm?.ervkUuid && pmTypeCode === ProfKindCodesEnum.PP) {
      getIsUnableToRefuse(reasonPm?.ervkUuid, recordId, recordVersionId);
    }
  };

  const addReason = useCallback(() => {
    if (!watchOnly && push) {
      const addingReason: IKnmPmFormReason = {
        reason: undefined,
        type: undefined,
        text: undefined,
        date: undefined,
        riskIndicators: undefined,
        requirementDetails: undefined,
        dateRequirement: undefined,
        assignmentNumber: undefined,
        assignmentDate: undefined,
        numGuid: undefined,
        dateCreate: undefined,
        dateBegin: undefined,
        dateEnd: undefined,
        order: undefined,
        knmIdLink: undefined,
        ervkUuid: undefined,
        links: undefined,
        listId: uuidv4(),
        planKnm: undefined,
        fullName: undefined,
      };

      push('reasonsPm', addingReason);
    }
  }, [push, watchOnly]);

  const makeRemoveReasonHandler = (index: number) => () => {
    if (!watchOnly && remove) {
      remove('reasonsPm', index);
      formRef?.current.change('objectsPm');
      formRef?.current.change('subjectTypePm');
      formRef?.current.change('isOrganizationNew');
      formRef?.current.change('ervkId');
    }
  };

  const isUseStartDate = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.REASON_LAST_CONDUCTED_KNM_DATE_VALIDATION_4_1_54_STARTS_FROM,
  );

  const isDisabled = !isNewKnm && !isKnmStatusFillInProcess && !isKnmStatusReadyForApprove && !isKnmStatusCommentsExist;

  const makeRemoveReasonOrder = (index: number) => () => {
    if (!watchOnly) {
      formRef?.current.change(`reasonsPm[${index}].order` as keyof IKnmPmForm);
      handleOrderChange(index)();
    }
  };

  const handleIsUnableToRefuseChange = useCallback((v: ChangeEvent<HTMLInputElement>) => {
    if (!v.target.checked) {
      formRef?.current.change('eventsPm');
    }
  }, []);

  const isAssignment = (reason: IKnmPmFormReason) => {
    if (isLaterThanOrderInProd) {
      return !!reason.order;
    }

    return reason.reason?.assignmentNumber && reason.reason?.assignmentDate && !reason.order;
  };

  const getReasonOptions = () => {
    const { knoOrganizationPm: knoOrganizationPmValue, supervisionTypePm: supervisionTypePmValue } =
      formRef?.current.getState().values ?? {};

    if (knoOrganizationPmValue && supervisionTypePmValue) {
      dispatch(
        catalogPmConditionsGet(
          String(knoOrganizationPmValue?.value.dictVersionId),
          String(supervisionTypePmValue?.value.dictVersionId),
        ),
      );
    }
  };

  const getOrderOptions = (reasonFieldValue: IKnmPmFormReason) => () => {
    const { knoOrganizationPm: knoOrganizationPmValue, supervisionTypePm: supervisionTypePmValue, pmType } =
      formRef?.current.getState().values ?? {};

    if (
      knoOrganizationPmValue &&
      supervisionTypePmValue &&
      pmType &&
      reasonFieldValue &&
      isAssignmentRequiredByType(reasonFieldValue.type)
    ) {
      const currentErvkUuid = reasonFieldValue?.ervkUuid;

      dispatch(
        knmGetOrders(
          String(knoOrganizationPmValue?.value.dictVersionId),
          String(supervisionTypePmValue?.value.dictVersionId),
          String(currentErvkUuid),
          true,
          pmType.value.dictVersionId,
        ),
      );
    }
  };

  const reasonOptions = useCallback(
    (reasonsFieldsValue: IKnmPmFormReason[], reasonFieldValue: IKnmPmFormReason) => {
      const typesSelectOptions: IKnmReasonTypeSelectOption[] | undefined = knmReasonTypesSelectOptions
        ?.filter(selectOption => {
          const dateBeginAsDate = parseDate({
            date: selectOption.dateBegin || '',
            parseFormat: SYSTEM_DATE_FORMAT_FNS,
          });
          const dateEndAsDate = parseDate({
            date: selectOption.dateEnd || '',
            parseFormat: SYSTEM_DATE_FORMAT_FNS,
          });

          if (requiredDatesFilled) {
            const startDateAsDate = parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

            if (stopDate) {
              const stopDateAsDate = parseDate({ date: stopDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

              if (selectOption.dateBegin && selectOption.dateEnd) {
                return (
                  isBefore(dateBeginAsDate || 0, startDateAsDate || 0) &&
                  isAfter(dateEndAsDate || 0, stopDateAsDate || 0)
                );
              }
              if (selectOption.dateBegin) {
                return isBefore(dateBeginAsDate || 0, startDateAsDate || 0);
              }
            } else {
              if (selectOption.dateBegin && selectOption.dateEnd) {
                return (
                  isBefore(dateBeginAsDate || 0, startDateAsDate || 0) &&
                  isAfter(dateEndAsDate || 0, startDateAsDate || 0)
                );
              }
              if (selectOption.dateBegin) {
                return isBefore(dateBeginAsDate || 0, startDateAsDate || 0);
              }
            }
          }

          return false;
        })
        .map(selectOption => ({
          ...selectOption,
          isDisabled: reasonsFieldsValue?.some(
            reasonsFieldsValueItem =>
              reasonsFieldsValueItem.type?.id === selectOption.id && selectOption.id !== reasonFieldValue.type?.id,
          ),
        }));

      if (reasonFieldValue?.type) {
        const areExists = typesSelectOptions?.some(option => option.id === reasonFieldValue.type?.id);

        if (!areExists) {
          return [...(typesSelectOptions || []), reasonFieldValue.type];
        }
      }

      return typesSelectOptions;
    },
    [knmReasonTypesSelectOptions, startDate, stopDate],
  );

  const parseStartDate = parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

  return (
    <div className={className ? `${styles.Wrapper} ${className}` : styles.Wrapper} id={PM_REASONS}>
      {!autoAddReason && (
        <h2 className="shared-title__with-margin">
          {getTitleFieldWithRedStar({
            label: 'Основание проведения ПМ',
            field: `reasonsPm`,
            isPm: true,
          })}
        </h2>
      )}

      <div className={styles.ReasonFieldBlock} id={REASONS_BLOCK_ID}>
        <div className="shared-group-label" style={autoAddReason ? { marginBottom: '10px' } : undefined}>
          Основание проведения мероприятия
          {autoAddReason && <span className={styles.redStar}>*</span>}
          {!autoAddReason && (
            <Button
              className="shared-btn-add"
              text="Добавить"
              variant="link"
              color="primary"
              type="button"
              id="pmReasonsAddButton"
              disabled={isReasonFieldsDisabled || !!reasonsPm?.length || !autoAddReason}
              onClick={addReason}
            />
          )}
        </div>
        <FieldArray name="reasonsPm">
          {({ fields }: FieldArrayRenderProps<IKnmPmFormReason, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return (
              <ul className={styles.ReasonsList}>
                {fields.map((reasonsFieldName, sIndex) => {
                  const reasonsFieldsValue: IKnmPmFormReason[] = fields.value;
                  const reasonFieldValue: IKnmPmFormReason = reasonsFieldsValue?.[sIndex];
                  const id = getReasonBlockId(reasonFieldValue.listId);
                  const { order } = reasonFieldValue;

                  const isActiveOrderHelpText =
                    order?.flagInagency || order?.flagIt || order?.flagMsp || order?.flagSonko;

                  const isReasonTypeFieldDisabled =
                    (!isLaterThanReasonFieldsAvailabilitySetting
                      ? isDisabledReasonPlanKnm
                      : !(isSupervisor && (isNewKnm || isKnmStatusFillInProcess))) || isWatchOnlyByTestUser;

                  const initialReasonType = initialReasonsPm?.find(
                    initialReason => initialReason.listId === reasonFieldValue.listId,
                  )?.type;

                  const IRAssignmentNumber = reasonFieldValue?.order?.inspectionResults?.find(
                    i => i.inspectedField === InspectionResultFieldEnum.ERVK_ORDER_NUMBER,
                  );
                  const IRAssignmentDate = reasonFieldValue?.order?.inspectionResults?.find(
                    i => i.inspectedField === InspectionResultFieldEnum.ERVK_ORDER_DATE,
                  );

                  return (
                    <li id={id} key={id}>
                      <div className="shared-field-row">
                        <div className="shared-deleting-row">
                          <SelectInputField
                            disabled={isReasonTypeFieldDisabled || !autoAddReason || disabled}
                            id={`${reasonsFieldName}.type`}
                            isShowTooltip={true}
                            isSearchable
                            name={`${reasonsFieldName}.type`}
                            options={reasonOptions(reasonsFieldsValue, reasonFieldValue)}
                            onOpen={getReasonOptions}
                            onChange={handleReasonTypeChange(sIndex)}
                            initialValue={reasonFieldValue?.type || initialReasonType}
                            defaultValue={reasonFieldValue?.type || initialReasonType}
                            placeholder={getTitleFieldWithRedStar({
                              label: getReasonsPlaceholder(),
                              field: `${reasonsFieldName}.type`,
                              isPm: true,
                            })}
                            size="medium"
                            tooltipWidth={document.getElementById(`${reasonsFieldName}.type`)?.offsetWidth}
                          />
                          {!isNewKnm && (
                            <>
                              {isPlanKnm(reasonFieldValue) && pmTypeCode !== ProfKindCodesEnum.OP && !autoAddReason && (
                                <PmReasonsLink
                                  formRef={formRef}
                                  isDisabledReasonPlanKnm={isReasonFieldsDisabled}
                                  reasonFieldValue={reasonFieldValue}
                                  reasonsFieldName={reasonsFieldName}
                                  sIndex={sIndex}
                                />
                              )}
                              {reasonFieldValue?.type?.hasText && !autoAddReason && (
                                <FieldBlock
                                  label={getTitleFieldWithRedStar({
                                    label: 'Дополнительно',
                                    field: `${reasonsFieldName}.text`,
                                    isPm: true,
                                  })}
                                  color="black"
                                  margin="top"
                                >
                                  <TextareaField
                                    className={styles.ReasonTextField}
                                    name={`${reasonsFieldName}.text`}
                                    disabled={isReasonFieldsDisabled}
                                  />
                                </FieldBlock>
                              )}
                              {reasonFieldValue?.type?.indicatorRiskRequired && !autoAddReason && (
                                <KnmReasonRiskIndicators
                                  reasonFieldValue={reasonFieldValue}
                                  reasonsFieldName={reasonsFieldName}
                                  isPm={true}
                                  formRef={formRef}
                                  isWatchOnly={isReasonFieldsDisabled}
                                />
                              )}
                              {(reasonFieldValue?.type?.requirementDetailsRequired ||
                                reasonFieldValue?.type?.detailsRequirement) &&
                                reasonFieldValue?.type?.dateRequirement &&
                                !autoAddReason && (
                                  <div className={styles.ReasonRiskIndikatorField}>
                                    <Row notWrap>
                                      <FieldBlock
                                        label={getTitleFieldWithRedStar({
                                          label: 'Дата требования',
                                          field: `${reasonsFieldName}.dateRequirement`,
                                          isPm: true,
                                        })}
                                        placementLabel="left"
                                        color="black"
                                        type="not-space"
                                      >
                                        <DatePickerField
                                          className={styles.ReasonDateField}
                                          id={`${reasonsFieldName}.dateRequirement`}
                                          name={`${reasonsFieldName}.dateRequirement`}
                                          placeholder="Выберите дату"
                                          disabled={isReasonFieldsDisabled}
                                          validate={makeRequired('Поле не может быть пустым')}
                                          maxDate={parseStartDate}
                                        />
                                      </FieldBlock>
                                      <FieldBlock
                                        label={getTitleFieldWithRedStar({
                                          label: 'Реквизиты требования',
                                          field: `${reasonsFieldName}.requirementDetails`,
                                          isPm: true,
                                        })}
                                        placementLabel="left"
                                        color="black"
                                        size="half"
                                      >
                                        <TextareaField
                                          className="shared-row-field"
                                          id={`${reasonsFieldName}.requirementDetails`}
                                          name={`${reasonsFieldName}.requirementDetails`}
                                          disabled={isReasonFieldsDisabled}
                                        />
                                      </FieldBlock>
                                    </Row>
                                  </div>
                                )}
                              {!reasonFieldValue?.type?.assignmentNumberRequired &&
                                !reasonFieldValue?.type?.approveRequired &&
                                reasonFieldValue?.type?.dateRequired &&
                                !autoAddReason && (
                                  <div className={styles.ReasonRiskIndikatorField}>
                                    <FieldBlock
                                      size="half"
                                      label={getTitleFieldWithRedStar({
                                        label: 'Дата',
                                        field: `${reasonsFieldName}.date`,
                                        isPm: true,
                                      })}
                                      placementLabel="left"
                                      color="black"
                                    >
                                      <DatePickerField
                                        className="shared-row-field"
                                        id={`${reasonsFieldName}.date`}
                                        name={`${reasonsFieldName}.date`}
                                        disabled={isReasonFieldsDisabled}
                                      />
                                    </FieldBlock>
                                  </div>
                                )}
                              {isAssignmentRequiredByType(reasonFieldValue.type) && !autoAddReason && (
                                <FieldBlock margin="top">
                                  <div className="shared-field-row">
                                    <div className="shared-row-flex">
                                      {!order && isLaterThanOrderInProd && (
                                        <Row>
                                          <FieldBlock
                                            label={getTitleFieldWithRedStar({
                                              label: 'Выберите поручение',
                                              field: `${reasonsFieldName}.order`,
                                              isPm: true,
                                            })}
                                            placementLabel="left"
                                            color="black"
                                            labelSize="col-4"
                                          >
                                            <SelectInputField
                                              className="shared-row-field"
                                              id={`${reasonsFieldName}.order`}
                                              name={`${reasonsFieldName}.order`}
                                              isSearchable
                                              disabled={isReasonFieldsDisabled}
                                              options={knmOrders}
                                              onOpen={getOrderOptions(reasonFieldValue)}
                                              onChange={handleOrderChange(sIndex)}
                                              size="medium"
                                              placeholder="Введите номер, дату или полное наименование поручения"
                                            />
                                          </FieldBlock>
                                          {isActiveOrderHelpText && (
                                            <Hint text="Статус лица в реестре противоречит требованиям поручения" />
                                          )}
                                        </Row>
                                      )}
                                      {isAssignment(reasonFieldValue) && (
                                        <>
                                          <Row notWrap>
                                            <FieldBlock
                                              size="half"
                                              label={getTitleFieldWithRedStar({
                                                label: 'Номер поручения',
                                                field: `${reasonsFieldName}.assignmentNumber`,
                                                isPm: true,
                                              })}
                                              placementLabel="left"
                                              color="black"
                                              margin="right"
                                            >
                                              <TextInputField
                                                className="shared-row-field"
                                                id={`${reasonsFieldName}.assignmentNumber`}
                                                name={`${reasonsFieldName}.assignmentNumber`}
                                                inspectionResult={IRAssignmentNumber}
                                                disabled={true}
                                              />
                                            </FieldBlock>
                                            <FieldBlock
                                              size="half"
                                              label={getTitleFieldWithRedStar({
                                                label: 'Дата поручения',
                                                field: `${reasonsFieldName}.assignmentDate`,
                                                isPm: true,
                                              })}
                                              placementLabel="left"
                                              color="black"
                                              margin="right"
                                            >
                                              <TextInputField
                                                className="shared-row-field"
                                                id={`${reasonsFieldName}.assignmentDate`}
                                                name={`${reasonsFieldName}.assignmentDate`}
                                                inspectionResult={IRAssignmentDate}
                                                disabled={true}
                                              />
                                            </FieldBlock>
                                          </Row>
                                          <Row>
                                            <FieldBlock
                                              label={getTitleFieldWithRedStar({
                                                label: 'ФИО лица, издавшего поручение',
                                                field: `${reasonsFieldName}.fullName`,
                                                isPm: true,
                                              })}
                                              color="black"
                                              placementLabel="left"
                                              labelSize="col-4"
                                            >
                                              <TextareaField
                                                resize="none"
                                                className="shared-row-field"
                                                name={`${reasonsFieldName}.fullName`}
                                                disabled={true}
                                              />
                                            </FieldBlock>
                                          </Row>
                                        </>
                                      )}
                                    </div>
                                    {order && !isReasonFieldsDisabled && (
                                      <CloseButton
                                        className="shared-deleting-row-btn"
                                        onClick={makeRemoveReasonOrder(sIndex)}
                                      />
                                    )}
                                  </div>
                                </FieldBlock>
                              )}
                              {reasonFieldValue?.type?.lastConductedKnmDateRequired && !autoAddReason && (
                                <div className={styles.ReasonDateFieldWrapper}>
                                  <Row>
                                    <FieldBlock
                                      size="half"
                                      label={getTitleFieldWithRedStar({
                                        label: 'Дата',
                                        field: `${reasonsFieldName}.lastConductedKnmDate`,
                                        isPm: true,
                                      })}
                                      placementLabel="left"
                                      color="black"
                                    >
                                      <DatePickerField
                                        className="shared-row-field"
                                        id={`${reasonsFieldName}.lastConductedKnmDate`}
                                        name={`${reasonsFieldName}.lastConductedKnmDate`}
                                        placeholder="Выберите дату"
                                        disabled={isDisabled}
                                        validate={
                                          isUseStartDate ? makeRequired('Поле не может быть пустым') : undefined
                                        }
                                      />
                                    </FieldBlock>
                                  </Row>
                                </div>
                              )}
                              {reasonFieldValue?.type?.linkOther && !autoAddReason && (
                                <ErknmLinks
                                  watchOnly={watchOnly}
                                  isEnabled={true}
                                  linksName={`${reasonsFieldName}.links`}
                                  reason={reasonFieldValue}
                                  created={created}
                                  reasonsFieldName={`${reasonsFieldName}`}
                                  controllingOrganizationIds={knoOrganizationPm?.idStr?.toString() || ''}
                                  kindControlCode={supervisionTypePm?.code || ''}
                                />
                              )}
                              {isShowCheckingProgram &&
                                (reasonFieldValue?.type?.programNumberRequired ||
                                  reasonFieldValue?.type?.programFileRequired) &&
                                !autoAddReason && (
                                  <FieldBlock margin="top">
                                    <div className="shared-field-row">
                                      <div className="shared-row-flex">
                                        {reasonFieldValue?.type?.programNumberRequired && (
                                          <Row>
                                            <FieldBlock
                                              label={getTitleFieldWithRedStar({
                                                label: 'Номер программы проверок',
                                                field: `${reasonsFieldName}.programNumber`,
                                                isPm: true,
                                              })}
                                              placementLabel="left"
                                              color="black"
                                            >
                                              <TextInputField
                                                className="shared-row-field"
                                                id={`${reasonsFieldName}.programNumber`}
                                                name={`${reasonsFieldName}.programNumber`}
                                                disabled={isReasonFieldsDisabled}
                                              />
                                            </FieldBlock>
                                          </Row>
                                        )}
                                        {reasonFieldValue?.type?.programFileRequired && (
                                          <ErknmReasonProgramDocument
                                            watchOnly={isReasonFieldsDisabled}
                                            name={`${reasonsFieldName}.programAttachment`}
                                            programAttachment={reasonFieldValue?.programAttachment}
                                            formRef={formRef}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </FieldBlock>
                                )}
                            </>
                          )}
                        </div>
                        {!isReasonFieldsDisabled && !autoAddReason && (
                          <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveReasonHandler(sIndex)} />
                        )}
                        {autoAddReason && (
                          <div className={styles.tooltipContainer}>
                            <Tooltip
                              placement="right"
                              type="info"
                              content={
                                <div className="shared-tooltip-info-content">
                                  Поле зависит от значения в следующих полях:
                                  <ul>
                                    <p>Контрольный (надзорный) орган</p>
                                    <p>Вид контроля (надзора)</p>
                                    <p>Даты проведения мероприятия</p>
                                  </ul>
                                </div>
                              }
                            >
                              <span className="shared-tooltip-info-icon" />
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
                {pmTypeCode === ProfKindCodesEnum.PP && !autoAddReason && (
                  <li id={IS_UNABLE_TO_REFUSE_BLOCK_ID}>
                    <CheckboxField
                      id="isUnableToRefuse"
                      name="isUnableToRefuse"
                      label="Обязательный профилактический визит (не предусматривающий возможность отказа)"
                      size="medium"
                      type="checkbox"
                      theme="system"
                      disabled={true}
                      className="shared-field-row_margin"
                      onChange={handleIsUnableToRefuseChange}
                    />
                  </li>
                )}
              </ul>
            );
          }}
        </FieldArray>
      </div>
      <PmReasonsPlanKnmMessageModal />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPmReasonsStoreProps => ({
  formRef: getKnmFormRef(state),
  knmReasonTypesSelectOptions: getPmReasonTypesSelectOptions(state),
  knmOrders: getKnmOrdersSelectOptions(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  settings: getAdminFetchSettings(state),
  isNewKnm: getKnmIsNew(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isKnmStatusCommentsExist: getKnmIsErknmStatusCommentsExist(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
});

export const PmReasons = connect(mapStateToProps)(PmReasonsWithoutStore);
